import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2, AlertTriangle, XCircle, X } from "lucide-react";

export type ToastType = "success" | "error" | "warning";

export interface ToastItem {
    id: string;
    message: string;
    type: ToastType;
    duration?: number;
}

// Global listeners registry for simple toast trigger
type ToastListener = (toast: ToastItem) => void;
const listeners = new Set<ToastListener>();

export const toast = {
    success(message: string, duration = 4000) {
        const id = Math.random().toString(36).substring(2, 9);
        listeners.forEach((listener) => listener({ id, message, type: "success", duration }));
    },
    error(message: string, duration = 4000) {
        const id = Math.random().toString(36).substring(2, 9);
        listeners.forEach((listener) => listener({ id, message, type: "error", duration }));
    },
    warning(message: string, duration = 4000) {
        const id = Math.random().toString(36).substring(2, 9);
        listeners.forEach((listener) => listener({ id, message, type: "warning", duration }));
    },
};

export function ToastContainer() {
    const [toasts, setToasts] = useState<ToastItem[]>([]);

    useEffect(() => {
        const addToast = (newToast: ToastItem) => {
            setToasts((prev) => [...prev, newToast]);
        };

        listeners.add(addToast);
        return () => {
            listeners.delete(addToast);
        };
    }, []);

    const removeToast = (id: string) => {
        setToasts((prev) => prev.filter((item) => item.id !== id));
    };

    return (
        <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none">
            <AnimatePresence mode="popLayout">
                {toasts.map((item) => (
                    <Toast key={item.id} item={item} onClose={removeToast} />
                ))}
            </AnimatePresence>
        </div>
    );
}

interface ToastProps {
    item: ToastItem;
    onClose: (id: string) => void;
}

function Toast({ item, onClose }: ToastProps) {
    useEffect(() => {
        const timer = setTimeout(() => {
            onClose(item.id);
        }, item.duration || 4000);

        return () => clearTimeout(timer);
    }, [item, onClose]);

    // Styling and Icon based on Type
    const styles = {
        success: {
            bg: "bg-emerald-500/60 border-emerald-500/30 text-emerald-400 dark:bg-emerald-950/60",
            icon: <CheckCircle2 className="w-5 h-5 text-emerald-400" />,
        },
        error: {
            bg: "bg-rose-500/20 border-rose-500/30 text-rose-400 dark:bg-rose-950/60",
            icon: <XCircle className="w-5 h-5 text-rose-400" />,
        },
        warning: {
            bg: "bg-amber-500/20 border-amber-500/30 text-amber-400 dark:bg-amber-950/60",
            icon: <AlertTriangle className="w-5 h-5 text-amber-400" />,
        },
    }[item.type];

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 50, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, scale: 0.85, transition: { duration: 0.15 } }}
            className={`pointer-events-auto flex items-start gap-3 p-4 rounded-xl border backdrop-blur-md shadow-lg ${styles.bg}`}
        >
            <div className="shrink-0 mt-0.5">{styles.icon}</div>
            <div className="flex-1 text-sm font-medium leading-relaxed select-none">
                {item.message}
            </div>
            <button
                onClick={() => onClose(item.id)}
                className="shrink-0 mt-0.5 p-0.5 rounded-lg hover:bg-black/10 dark:hover:bg-white/10 transition-colors"
            >
                <X className="w-4 h-4 opacity-70 hover:opacity-100" />
            </button>
        </motion.div>
    );
}
